// DEPENDENCIES
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
// COMPONENTS
import { Link } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import MUIDataTable from 'mui-datatables';
import SVG from 'react-inlinesvg';
import { OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
// CUSTOM COMPONENTS
import * as alert from '../../../redux/alertSnackBarRedux';
import * as confirmModal from '../../../redux/confirmModalRedux';
import DateFilterComponent from '../../components/DateFilterComponent';
// SERVICES AND HELPERS
import * as userCredentialService from '../../../services/training/userCredentialService';
import {
  dashIfNull,
  getCredentialBadgeColor,
  getCredentialExpiresInColor,
  dateFilterLogic,
  dateFilterListRenderer,
} from '../../../helpers/helper';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';

const CredentialsListingPage = (props) => {
  const { showAlert, showConfirmModal, hideConfirmModal, setProcessing, auth } = props;
  const [data, setData] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTableData();
  }, [showArchived]);

  const loadTableData = () => {
    userCredentialService
      .getUserCredentials(showArchived)
      .then((res) => {
        setData(res);
      })
      .catch((ex) => {
        showAlert({ message: ex.message, variant: 'error' });
      })
      .finally(() => setLoading(false));
  };

  const archiveCredentialToggle = (userCredentialId) => {
    setProcessing();
    userCredentialService
      .toggleUserCredentialArchive(userCredentialId)
      .then((res) => {
        showAlert({ message: res.message, variant: 'success' });
        loadTableData();
      })
      .catch((ex) => showAlert({ message: ex.message, variant: 'error' }))
      .finally(() => hideConfirmModal());
  };

  const columns = [
    {
      name: 'id',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        width: 1,
        download: false,
        print: false,
        display: auth.isAdmin,
        setCellProps: () => ({
          'data-aes-field': 'actions',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'actions',
        }),
        customBodyRenderLite: (dataIndex) => {
          return (
            <div className='aes-admin-listing-actions'>
              <NavLink
                to={'/training/user-credentials/edit-credential/' + data[dataIndex].id}
                className='btn btn-sm btn-info font-weight-bold font-size-sm mr-3'
              >
                Edit
              </NavLink>
              {data[dataIndex].isArchived ? (
                <button
                  onClick={() =>
                    showConfirmModal({
                      title: 'Unarchive User Credential',
                      text: 'Are you sure you want to unarchive this credential?',
                      rightBtnText: 'Confirm',
                      btnAction: () => archiveCredentialToggle(data[dataIndex].id),
                    })
                  }
                  className='btn btn-sm btn-warning font-weight-bold font-size-sm mr-3'
                >
                  Unarchive
                </button>
              ) : (
                <button
                  onClick={() =>
                    showConfirmModal({
                      title: 'Archive User Credential',
                      text: 'Are you sure you want to archive this credential?',
                      rightBtnText: 'Confirm',
                      btnAction: () => archiveCredentialToggle(data[dataIndex].id),
                    })
                  }
                  className='btn btn-sm bg-yellow c-white font-weight-bold font-size-sm mr-3'
                >
                  Archive
                </button>
              )}
            </div>
          );
        },
      },
    },
    {
      name: 'name',
      label: 'Employee Name',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'primary',
          'data-aes-label': 'Employee Name',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'primary',
        }),
        customBodyRenderLite: (dataIndex) => {
          return (
            <div className='user-info-block'>
              <Link
                to={'/management/user-profile/' + data[dataIndex].userId}
                className='text-dark font-weight-bolder text-hover-primary mb-1 font-size-lg'
              >
                {data[dataIndex].name}
              </Link>
            </div>
          );
        },
      },
    },
    {
      name: 'type',
      label: 'Type',
      options: {
        filter: true,
        sort: true,
        filterType: 'multiselect',
        setCellProps: () => ({
          'data-aes-field': 'secondary-one',
          'data-aes-label': 'Type',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'secondary-one',
        }),
      },
    },
    {
      name: 'subType',
      label: 'Sub Type',
      options: {
        filter: true,
        sort: true,
        filterType: 'multiselect',
        setCellProps: () => ({
          'data-aes-field': 'secondary-two',
          'data-aes-label': 'Sub Type',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'secondary-two',
        }),
        customBodyRenderLite: (dataIndex) => {
          return <p>{dashIfNull(data[dataIndex].subType)}</p>;
        },
      },
    },
    {
      name: 'credential',
      label: 'Credential',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'detail',
          'data-aes-label': 'Credential',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'detail',
        }),
        customBodyRenderLite: (dataIndex) => {
          let colorArr = getCredentialBadgeColor(data[dataIndex].expiryDate);
          return (
            <div className='tc-user-block'>
              <div className='user-info-block'>
                <div className='flex-container align-items-center'>
                  <span className={`small-badge ${colorArr[1]}`}>{colorArr[0]}</span>
                  {data[dataIndex].credential}
                </div>
              </div>
            </div>
          );
        },
      },
    },
    {
      name: 'effectiveDate',
      label: 'Effective Date',
      options: {
        filter: true,
        sort: true,
        filterType: 'custom',
        setCellProps: () => ({
          'data-aes-field': 'detail-one',
          'data-aes-label': 'Effective Date',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'detail-one',
        }),
        customFilterListRender: (v) => dateFilterListRenderer(v),
        filterOptions: {
          names: [],
          logic(date, filters) {
            return dateFilterLogic(date, filters);
          },
          display: (filterList, onChange, index, column) => (
            <DateFilterComponent name={'Effective Date'} filterList={filterList} onChange={onChange} index={index} column={column} />
          ),
        },
      },
    },
    {
      name: 'expiryDate',
      label: 'Expiry Date',
      options: {
        filter: true,
        sort: true,
        filterType: 'custom',
        setCellProps: () => ({
          'data-aes-field': 'detail-two',
          'data-aes-label': 'Expiry Date',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'detail-two',
        }),
        customFilterListRender: (v) => dateFilterListRenderer(v),
        filterOptions: {
          names: [],
          logic(date, filters) {
            return dateFilterLogic(date, filters);
          },
          display: (filterList, onChange, index, column) => (
            <DateFilterComponent name={'Expiry Date'} filterList={filterList} onChange={onChange} index={index} column={column} />
          ),
        },
      },
    },
    {
      name: 'siteCode',
      label: 'Site Code',
      options: {
        filter: true,
        filterType: 'multiselect',
        sort: true,
        setCellProps: () => ({
          'data-aes-field': 'detail-three',
          'data-aes-label': 'Site Code',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'detail-three',
        }),
        customBodyRenderLite: (dataIndex) => {
          return <p>{dashIfNull(data[dataIndex].siteCode)}</p>;
        },
      },
    },
    {
      name: 'expiryDate',
      label: 'Expires In',
      options: {
        filter: false,
        sort: true,
        download: false,
        setCellProps: () => ({
          'data-aes-field': 'detail-four',
          'data-aes-label': 'Expires In',
        }),
        setCellHeaderProps: () => ({
          'data-aes-field': 'detail-four',
        }),
        customBodyRenderLite: (dataIndex) => {
          let colorArr = getCredentialExpiresInColor(data[dataIndex].expiryDate);

          return (
            <>
              <div className='circle-badge-container'>
                <p>
                  <span className={`circle-badge ${colorArr[2]}`}>{colorArr[0]}</span> {colorArr[1]}
                </p>
              </div>
            </>
          );
        },
      },
    },
  ];

  return (
    <div className='row aes-admin-listing-page aes-admin-listing-page--user-credentials'>
      <div className='col-lg-12 col-xxl-12'>
        <div className={`card card-custom card-stretch gutter-b}`}>
          <div className='card-header border-0 py-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label font-weight-bolder text-dark archive-unarchive-span'>
                User Credentials
                <OverlayTrigger
                  key={'right'}
                  placement={'right'}
                  overlay={
                    <Tooltip id={`tooltip-right`}>
                      Switch to <strong> {showArchived ? 'active' : 'archived'}</strong>
                    </Tooltip>
                  }
                >
                  <span
                    onClick={() => setShowArchived(!showArchived)}
                    style={{ cursor: 'pointer' }}
                    className={`label label-xl label-inline label-light-${!showArchived ? 'success' : 'warning'}`}
                  >
                    {!showArchived ? 'Active ' : 'Archived'}
                  </span>
                </OverlayTrigger>
              </span>
              <span className='text-muted mt-3 font-weight-bold fz-xs'>View and Manage User Credentials</span>
            </h3>

            {auth.isAdmin && (
              <div className='card-toolbar'>
                <NavLink
                  to={'/training/user-credentials/edit-credential/' + 0}
                  className='btn btn-success font-weight-bolder font-size-sm mr-3'
                >
                  <span className='svg-icon svg-icon-lg svg-icon-white'>
                    <SVG src={toAbsoluteUrl('/media/svg/icons/Code/Plus.svg')} />
                  </span>{' '}
                  Add Credential
                </NavLink>
              </div>
            )}
          </div>
          <div className='container-fluid aes-admin-listing p-0'>
            <MUIDataTable
              className='aes-admin-listing-surface'
              data={data}
              columns={columns}
              options={{
                responsive: 'standard',
                rowsPerPage: 25,
                rowsPerPageOptions: [10, 15, 20, 25, 30, 35, 40, 45, 50, 100],
                selectableRows: 'none',
                setTableProps: () => ({
                  className: 'aes-admin-listing-table',
                }),
                downloadOptions: {
                  filename: 'User_Credentials.csv',
                  filterOptions: {
                    useDisplayedColumnsOnly: true,
                    useDisplayedRowsOnly: true,
                  },
                },
                textLabels: {
                  body: {
                    noMatch: loading ? <Spinner animation='border' size='lg' variant='info' /> : 'Sorry, no matching records found.',
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { ...alert.actions, ...confirmModal.actions })(CredentialsListingPage);
